export type Language = "cs" | "en";

export const LANGUAGE_STORAGE_KEY = "softiqLanguage";

export const languages: Array<{
  code: Language;
  flag: string;
  label: string;
  shortLabel: string;
}> = [
  { code: "cs", flag: "CZ", label: "Čeština", shortLabel: "CZ" },
  { code: "en", flag: "EN", label: "English", shortLabel: "EN" },
];

type TranslationPair = readonly [english: string, czech: string];
type TranslationSource = {
  readonly [key: string]: TranslationPair | TranslationSource;
};

type TranslationTree<T> = T extends TranslationPair
  ? string
  : {
      readonly [Key in keyof T]: TranslationTree<T[Key]>;
    };

const translationPairs = {
  common: {
    close: ["Close", "Zavřít"],
    genericSupportError: [
      "Something went wrong. Please contact support.",
      "Něco se porouchalo, napište nám na podporu.",
    ],
    softiq: ["SOFTIQ", "SOFTIQ"],
  },
  cookieBanner: {
    acceptAll: ["Accept all", "Přijmout vše"],
    analytical: [
      "We use analytics cookies to understand traffic, campaigns, and conversions.",
      "Používáme analytické cookies, ať máme přehled o návštěvnosti, kampaních a konverzi.",
    ],
    message: [
      "We use cookies to keep the website working properly.",
      "Pro správné fungování webu používáme cookies.",
    ],
    more: ["More", "Více"],
    reject: ["Reject", "Odmítnout"],
    saveSelection: ["Save selection", "Uložit výběr"],
    technical: [
      "Technical cookies are required for the website to work correctly.",
      "Technické cookies jsou nezbytné pro správné fungování webu.",
    ],
  },
  navbar: {
    newFeature: ["Request a new feature", "Požádat o novou funkci"],
    bugReport: ["Report a bug", "Nahlásit chybu"],
    cookies: ["Cookie settings", "Nastavení cookies"],
    language: ["Language", "Jazyk"],
    preferences: ["Preferences", "Předvolby"],
    settings: ["Settings", "Nastavení"],
    themeMode: {
      dark: ["Dark mode", "Tmavý režim"],
      light: ["Light mode", "Světlý režim"],
      toggle: ["Toggle dark mode", "Přepnout tmavý režim"],
    },
  },
  preferences: {
    advancePayments: ["Advance payments", "Zálohy"],
    close: ["Close", "Zavřít"],
    cookies: ["Cookies", "Cookies"],
    description: [
      "Manage language, appearance, cookies, and hidden content.",
      "Spravujte jazyk, vzhled, cookies a skrytý obsah.",
    ],
    electricity: ["Electricity", "Elektřina"],
    expanded: ["Expanded", "Rozbaleno"],
    estate: ["Property", "Nemovitost"],
    hidden: ["Hidden", "Skryto"],
    homepage: ["Homepage", "Domovská stránka"],
    hintAnalyticsView: [
      "Price development tip",
      "Tip k zobrazení vývoje ceny",
    ],
    hintPortfolioOverview: ["Portfolio tips", "Tipy k portfoliu"],
    hintPortfolioPerformance: [
      "Property performance help",
      "Nápověda k výkonnosti nemovitosti",
    ],
    hints: ["Help and tips", "Nápověda a tipy"],
    hintTransactionImport: [
      "Bank statement import tip",
      "Tip k importu bankovního výpisu",
    ],
    hintTransactionHistoryImport: [
      "Historical bank statement import tip",
      "Tip k importu staršího bankovního výpisu",
    ],
    hintWatchdogOverview: ["Watchdog tips", "Tipy k hlídacímu psovi"],
    hintWatchdogPriceDevelopment: [
      "Price development help",
      "Nápověda k vývoji ceny",
    ],
    hintWatchdogResults: [
      "Watchdog results help",
      "Nápověda k výsledkům hlídacího psa",
    ],
    hintWatchdogFavorites: [
      "Watchdog favorites tip",
      "Tip k oblíbeným nabídkám",
    ],
    insurance: ["Insurance", "Pojištění"],
    internet: ["Internet", "Internet"],
    language: ["Language", "Jazyk"],
    lightMode: ["Light", "Světlý"],
    manageCookies: ["Reset cookie settings", "Resetovat nastavení cookies"],
    darkMode: ["Dark", "Tmavý"],
    mode: ["Appearance", "Vzhled"],
    noEstates: [
      "Add a property to manage its transaction suggestions.",
      "Pro správu návrhů transakcí nejprve přidejte nemovitost.",
    ],
    portfolio: ["Portfolio overview", "Přehled portfolia"],
    collapsed: ["Collapsed", "Sbaleno"],
    rent: ["Rent", "Nájem"],
    suggestedTransactions: [
      "Suggested transactions",
      "Navrhované transakce",
    ],
    title: ["Preferences", "Předvolby"],
    visible: ["Visible", "Viditelné"],
    watchdog: ["Watchdog overview", "Přehled hlídacího psa"],
    water: ["Water", "Voda"],
  },
  reportBug: {
    cancel: ["Cancel", "Zrušit"],
    close: ["Close bug report", "Zavřít hlášení chyby"],
    description: [
      "Tell us what went wrong. We will review it and fix it so it does not happen again.",
      "Řekněte nám, co se nepovedlo. My se na to podíváme a opravíme, aby se to příště nestalo.",
    ],
    messageLabel: ["Message", "Zpráva"],
    placeholder: ["Your message", "Vaše zpráva"],
    submit: ["Send", "Odeslat"],
    title: ["Report a bug", "Nahlaste nám chybu"],
  },
} as const satisfies TranslationSource;

function resolveTranslations<T extends TranslationSource>(
  source: T,
  language: Language,
): TranslationTree<T> {
  const languageIndex = language === "en" ? 0 : 1;
  const result: Record<string, unknown> = {};

  Object.entries(source).forEach(([key, value]) => {
    result[key] = isTranslationPair(value)
      ? value[languageIndex]
      : resolveTranslations(value, language);
  });

  return result as TranslationTree<T>;
}

function isTranslationPair(
  value: TranslationPair | TranslationSource,
): value is TranslationPair {
  return Array.isArray(value);
}

export const translations = {
  cs: resolveTranslations(translationPairs, "cs"),
  en: resolveTranslations(translationPairs, "en"),
};
